import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FaIcons from 'react-icons/fa';

import ContentCarousel from '../components/common/ContentCarousel';
import AuthModal from '../components/common/AuthModal';
import apiClient from '../api/apiClient';

const GRADIENT_CLASSES = [
  'bg-gradient-to-r from-purple-700 to-indigo-900',
  'bg-gradient-to-r from-fuchsia-700 to-pink-900',
  'bg-gradient-to-r from-blue-700 to-sky-900',
  'bg-gradient-to-r from-teal-700 to-emerald-900',
  'bg-gradient-to-r from-green-700 to-lime-900',
  'bg-gradient-to-r from-rose-700 to-red-900',
];

const getIconComponent = (iconName) => {
  if (!iconName) return FaIcons.FaTh;
  return FaIcons[iconName] || FaIcons.FaTh;
};

const CategoryButton = ({ iconName, title, slug, gradientClass }) => {
  const Icon = getIconComponent(iconName);
  const bgClass = gradientClass || 'bg-gray-500';

  return (
    <Link
      to={`/products/list/${slug}`}
      className={`flex items-center justify-center h-[60px] border-none rounded-[10px] 
                  text-[11px] font-medium text-white cursor-pointer transition-colors 
                  text-right px-[10px] ${bgClass} w-[calc(50%-3px)]`}
    >
      <Icon className="text-base ml-2" />
      <span>{title}</span>
    </Link>
  );
};

const BannerFirst = () => (
  <Link to="/products/list/tag/Seneca" className="block w-full my-6 transition-transform hover:scale-[1.01] duration-300">
    <img src="https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/1.png" alt="مجموعه درس‌گفتارهای سنکا" className="w-full rounded-xl shadow-md object-cover" loading="lazy" />
  </Link>
);

const BannerSecond = () => (
  <Link to="/products/list/tag/A-lecture-on-philosophy-and-the-meaning-of-life" className="block w-full my-6 transition-transform hover:scale-[1.01] duration-300">
    <img src="https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/6.png" alt="مجموعه درس‌گفتارهای پوچی زندگی" className="w-full rounded-xl shadow-md object-cover" loading="lazy" />
  </Link>
);

const BannerThird = () => (
  <Link to="/products/list/tag/Maybe-a-little-healing" className="block w-full my-6 transition-transform hover:scale-[1.01] duration-300">
    <img src="https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/4.png" alt="معرفی کتاب" className="w-full rounded-xl shadow-md object-cover" loading="lazy" />
  </Link>
);

const HomePage = () => {
  const [mainCategories, setMainCategories] = useState([]);
  const [carousels, setCarousels] = useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [isCarouselsLoading, setIsCarouselsLoading] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get('/products/categories/main/');
        setMainCategories(response.data || []);
      } catch {
        // silently fail
      } finally {
        setIsCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (mainCategories.length === 0) return;

    const fetchHomeData = async () => {
      setIsCarouselsLoading(true);
      try {
        const dataPromises = mainCategories.map((category) =>
          apiClient.get(`products/list/${category.slug}/`, { params: { limit: 10 } })
        );
        const responses = await Promise.all(dataPromises);

        let newCarouselsData = responses.map((response, index) => {
          const category = mainCategories[index];
          const products = response.data.results || response.data;
          return { ...category, products: Array.isArray(products) ? products : [] };
        });

        newCarouselsData = newCarouselsData
          .map(carousel => ({
            ...carousel,
            maxProductId: carousel.products.length > 0
              ? Math.max(...carousel.products.map(p => p.id))
              : 0
          }))
          .sort((a, b) => b.maxProductId - a.maxProductId);

        setCarousels(newCarouselsData);
      } catch {
        // silently fail
      } finally {
        setIsCarouselsLoading(false);
      }
    };

    fetchHomeData();
  }, [mainCategories]);

  const handleContentClick = () => {
    const userIsLoggedIn = false;
    if (!userIsLoggedIn) {
      setIsAuthModalOpen(true);
    }
  };

  if (isCategoriesLoading) {
    return (
      <div className="flex justify-center items-center h-40 text-gray-600">
        در حال بارگذاری دسته‌بندی‌ها...
      </div>
    );
  }

  const firstCarousel = carousels[0];
  const secondCarousel = carousels[1];
  const thirdCarousel = carousels[2];
  const remainingCarousels = carousels.slice(3);

  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';

  return (
    <>
      <Helmet>
        <title>فرزانه | دروازه‌ای به فلسفه و علوم انسانی</title>
        <meta name="description" content="وب‌اپلیکیشن فرزانه: درس‌گفتارها، کتاب‌ها، دوره‌های آموزشی و محتوای اختصاصی از مصطفی ملکیان و دیگر اندیشمندان. فلسفه، روانشناسی، هنر زندگی." />
        <meta property="og:title" content="فرزانه | دروازه‌ای به فلسفه و علوم انسانی" />
        <meta property="og:description" content="مجموعه‌ای غنی از محتوای فلسفی، روان‌شناختی و هنر زندگی؛ همراه با امکانات پیشرفته مطالعه و یادگیری." />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="فرزانه | دروازه‌ای به فلسفه و علوم انسانی" />
        <meta name="twitter:description" content="درس‌گفتارها، کتاب‌ها و دوره‌های آموزشی با رویکردی عمیق به فلسفه و روانشناسی." />
        <meta name="twitter:image" content={defaultOgImage} />
      </Helmet>

      <div className="flex flex-col gap-5 p-0" style={{ paddingBottom: '80px' }}>
        <div className="flex flex-wrap gap-[6px] rtl justify-start p-2">
          {mainCategories.map((cat, idx) => {
            const gradient = GRADIENT_CLASSES[idx % GRADIENT_CLASSES.length];
            return (
              <CategoryButton
                key={cat.slug}
                iconName={cat.icon_name}
                title={cat.name}
                slug={cat.slug}
                gradientClass={gradient}
              />
            );
          })}
        </div>

        <div className="flex mt-[10px] gap-5 rtl pt-[5px] pb-[5px] justify-center">
          <Link to="/products/list?type=EBOOK" className="px-[35px] py-[10px] rounded-[20px] text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap border-none bg-[#dcdcdc] text-[#3a3a3a]">آثار مکتوب</Link>
          <Link to="/products/list?type=VIDEO_COURSE" className="px-[35px] py-[10px] rounded-[20px] text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap border-none bg-[#dcdcdc] text-[#3a3a3a]">ویدیوها</Link>
          <Link to="/products/list?type=LECTURE" className="px-[35px] py-[10px] rounded-[20px] text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap border-none bg-[#dcdcdc] text-[#3a3a3a]">صوت‌ها</Link>
        </div>

        {isCarouselsLoading ? (
          <div className="text-[14px] text-center text-gray-500 py-4">در حال بارگذاری محتوا...</div>
        ) : (
          <>
            {firstCarousel?.products?.length > 0 && (
              <ContentCarousel key={firstCarousel.slug} title={`تـازه‌تـرین در ${firstCarousel.name}`} data={firstCarousel.products} onCardClick={handleContentClick} querySlug={firstCarousel.slug} />
            )}
            <BannerFirst />
            {secondCarousel?.products?.length > 0 && (
              <ContentCarousel key={secondCarousel.slug} title={`تـازه‌تـرین در ${secondCarousel.name}`} data={secondCarousel.products} onCardClick={handleContentClick} querySlug={secondCarousel.slug} />
            )}
            <BannerSecond />
            {thirdCarousel?.products?.length > 0 && (
              <ContentCarousel key={thirdCarousel.slug} title={`تـازه‌تـرین در ${thirdCarousel.name}`} data={thirdCarousel.products} onCardClick={handleContentClick} querySlug={thirdCarousel.slug} />
            )}
            <BannerThird />
            {remainingCarousels.map((carousel) =>
              carousel.products.length > 0 ? (
                <ContentCarousel key={carousel.slug} title={`تـازه‌تـرین در ${carousel.name}`} data={carousel.products} onCardClick={handleContentClick} querySlug={carousel.slug} />
              ) : null
            )}
          </>
        )}
      </div>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
};

export default HomePage;